import React, { useCallback, useMemo, useState } from "react";
import { Button, IconButton, Tooltip } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  IWAServiceProvider,
  IShareViaWhatsAppButtonProps,
  SHARE_VIA_WHATSAPP_MODULE_MESSAGES,
} from "./ShareViaWhatsApp.types";
import { ChooseWhatsAppDIalog } from "./ChooseWhatsAppDIalog";
import { useAuth } from "../../contexts/AuthProvider";
import { isBaileyProvider, isMetaProvider } from "../../contexts/AuthProvider/AuthProvider.helpers";
import { sendGenericWhatsAppTemplateAsync } from "./sendGenericWhatsAppTemplate.service";
import { useDispatchWrapper } from "../../hooks";
import { showMessage } from "../../redux/messages/messagesActions";

export const ShareViaWhatsAppButton: React.FC<IShareViaWhatsAppButtonProps> =
  React.memo(({
    request,
    module,
    type = "icon",
    size = "small",
    disabled = false,
    tooltip,
  }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [sending, setSending] = useState(false);

    const dispatch = useDispatchWrapper();
    const { whatsappService } = useAuth();

    const isMetaEnabled = isMetaProvider(whatsappService);
    const isBaileyEnabled = isBaileyProvider(whatsappService);
    const hasAnyProvider = isMetaEnabled || isBaileyEnabled;
    const hasRequest = Boolean(request);

    // Derive messages from module constant, fall back to generic defaults
    const moduleMessages = useMemo(
      () =>
        module
          ? SHARE_VIA_WHATSAPP_MODULE_MESSAGES[module]
          : { success: "Sent successfully via WhatsApp", error: "Failed to send via WhatsApp", tooltip: "Send via WhatsApp" },
      [module],
    );

    const isDisabled = disabled || sending || !hasRequest || !hasAnyProvider;

    const resolvedTooltip =
      tooltip ??
      (!hasRequest
        ? "Save data to send via WhatsApp"
        : !hasAnyProvider
          ? "Configure WhatsApp QR or Meta WhatsApp to send messages."
          : moduleMessages.tooltip);

    const doSend = useCallback(
      async (provider?: IWAServiceProvider) => {
        if (!request) return;
        // Resolve the platform to send on:
        //  - explicit dialog choice (multi-provider case) takes priority
        //  - otherwise fall back to the single configured selectedService
        const platform = provider ?? whatsappService.selectedService ?? undefined;
        setSending(true);
        try {
          await sendGenericWhatsAppTemplateAsync({
            ...request,
            ...(platform ? { platform } : {}),
          });
          dispatch(showMessage({ type: "success", message: moduleMessages.success, displayAs: "snackbar" }));
        } catch {
          dispatch(showMessage({ type: "error", message: moduleMessages.error, displayAs: "snackbar" }));
        } finally {
          setSending(false);
        }
      },
      [request, dispatch, moduleMessages, whatsappService.selectedService],
    );

    const handleClick = useCallback(() => {
      if (isDisabled) return;
      if (whatsappService.isMultiple) {
        setDialogOpen(true);
      } else {
        doSend();
      }
    }, [isDisabled, whatsappService.isMultiple, doSend]);

    const handleDialogSelect = useCallback(
      (provider: IWAServiceProvider) => {
        setDialogOpen(false);
        // Forward the user's explicit choice so it lands in the payload
        doSend(provider);
      },
      [doSend],
    );

    const handleDialogClose = useCallback(() => setDialogOpen(false), []);

    const trigger =
      type === "button" ? (
        <Button
          size={size}
          onClick={handleClick}
          disabled={isDisabled}
          variant="outlined"
          startIcon={<WhatsAppIcon />}
          sx={{
            color: "#25D366",
            borderColor: "#25D366",
            borderWidth: "2px",
            "&:hover": {
              borderWidth: "2px",
              borderColor: "#1ebe5d",
              bgcolor: "rgba(37, 211, 102, 0.04)",
            },
            // Keep the button readable even when disabled (e.g. on dark panels)
            "&.Mui-disabled": {
              color: "#25D366",
              borderColor: "#25D366",
              borderWidth: "2px",
              opacity: 0.55,
            },
            textTransform: "none",
            fontWeight: 500,
          }}
        >
          {sending ? "Sending..." : "Share on WhatsApp"}
        </Button>
      ) : (
        <IconButton
          size={size}
          onClick={handleClick}
          disabled={isDisabled}
          sx={{
            color: "#25D366",
            "&:hover": { bgcolor: "rgba(37, 211, 102, 0.08)" },
            // Keep the icon visible even when disabled (e.g. on dark panels)
            "&.Mui-disabled": {
              color: "#25D366",
              opacity: 0.55,
            },
          }}
        >
          <WhatsAppIcon fontSize={size === "small" ? "small" : "medium"} />
        </IconButton>
      );

    return (
      <>
        <Tooltip title={resolvedTooltip} arrow>
          <span>{trigger}</span>
        </Tooltip>

        <ChooseWhatsAppDIalog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSelect={handleDialogSelect}
          providers={whatsappService.serviceProviders}
        />
      </>
    );
  });

ShareViaWhatsAppButton.displayName = "ShareViaWhatsAppButton";
